import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import MarketingLayout from './MarketingLayout';
import { getArticle, getArticleSection, SECTIONS } from '../data/articles';

export default function KnowledgeCenterArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = getArticle(slug);
  const sectionInfo = getArticleSection(slug);

  if (!article) {
    return (
      <MarketingLayout>
        <div style={{ textAlign: 'center', padding: '120px 24px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>Article not found</h1>
          <p style={{ color: '#64748b', marginBottom: 24 }}>This article doesn't exist or may have moved.</p>
          <Link to="/knowledge-center" style={{ background: '#2563eb', color: '#fff', textDecoration: 'none', padding: '10px 24px', borderRadius: 8, fontSize: 14, fontWeight: 600 }}>
            Back to Knowledge Center
          </Link>
        </div>
      </MarketingLayout>
    );
  }

  const section = sectionInfo?.section;

  // Find prev/next articles within same section
  const sectionArticles = section?.articles || [];
  const currentIdx = sectionArticles.findIndex(a => a.slug === slug);
  const prevArticle = currentIdx > 0 ? sectionArticles[currentIdx - 1] : null;
  const nextArticle = currentIdx < sectionArticles.length - 1 ? sectionArticles[currentIdx + 1] : null;

  const canonicalUrl = `https://clicksaver.app/knowledge-center/${slug}`;

  return (
    <MarketingLayout>
      <Helmet>
        <title>{article.title} — ClickSaver Knowledge Center</title>
        <meta name="description" content={article.intro} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${article.title} — ClickSaver`} />
        <meta property="og:description" content={article.intro} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.intro,
          "url": canonicalUrl,
          "publisher": { "@type": "Organization", "name": "ClickSaver", "url": "https://clicksaver.app" },
          "mainEntityOfPage": canonicalUrl,
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "ClickSaver",
          "url": "https://clicksaver.app",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "Google Ads click fraud protection software. Detects and blocks bots, competitors and click farms in real time.",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "148",
            "bestRating": "5",
            "worstRating": "1"
          }
        })}</script>
      </Helmet>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 80px', display: 'grid', gridTemplateColumns: '240px 1fr', gap: 40, alignItems: 'start' }}>

        {/* ── Sidebar ── */}
        <aside style={{ position: 'sticky', top: 80 }}>
          {/* Back link */}
          <Link
            to="/knowledge-center"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b', textDecoration: 'none', marginBottom: 24, fontWeight: 500 }}
          >
            ← Knowledge Center
          </Link>

          {/* Category nav */}
          {SECTIONS.map((s, i) => (
            <div key={i} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 6, paddingLeft: 8 }}>
                {s.title}
              </div>
              {s.articles.map((a, j) => (
                <Link
                  key={j}
                  to={`/knowledge-center/${a.slug}`}
                  style={{
                    display: 'block', padding: '7px 10px', fontSize: 13, textDecoration: 'none',
                    borderRadius: 6, marginBottom: 1,
                    color: a.slug === slug ? s.color : '#475569',
                    background: a.slug === slug ? s.bg : 'transparent',
                    fontWeight: a.slug === slug ? 600 : 400,
                    borderLeft: a.slug === slug ? `3px solid ${s.color}` : '3px solid transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (a.slug !== slug) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (a.slug !== slug) e.currentTarget.style.background = 'transparent'; }}
                >
                  {a.title}
                </Link>
              ))}
            </div>
          ))}
        </aside>

        {/* ── Article content ── */}
        <main>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8', marginBottom: 24 }}>
            <Link to="/knowledge-center" style={{ color: '#94a3b8', textDecoration: 'none' }}>Knowledge Center</Link>
            <span>›</span>
            <span style={{ color: section?.color }}>{article.category}</span>
            <span>›</span>
            <span style={{ color: '#475569' }}>{article.title}</span>
          </div>

          {/* Header */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
                padding: '3px 10px', borderRadius: 20, background: section?.bg, color: section?.color,
              }}>
                {article.category}
              </span>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{article.time} read</span>
            </div>
            <h1 style={{ fontSize: 34, fontWeight: 800, color: '#0f172a', marginBottom: 16, lineHeight: 1.2 }}>
              {article.title}
            </h1>
            <p style={{ fontSize: 16, color: '#475569', lineHeight: 1.7, borderLeft: `3px solid ${section?.color || '#2563eb'}`, paddingLeft: 16 }}>
              {article.intro}
            </p>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', marginBottom: 32 }} />

          {/* Sections */}
          {article.sections.map((sec, i) => (
            <div key={i} style={{ marginBottom: 36 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
                {sec.heading}
              </h2>

              {sec.content && (
                <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: sec.steps || sec.code ? 16 : 0 }}>
                  {sec.content}
                </p>
              )}

              {sec.steps && (
                <ol style={{ margin: '0 0 16px', paddingLeft: 20 }}>
                  {sec.steps.map((step, j) => (
                    <li key={j} style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 8, paddingLeft: 4 }}>
                      {step}
                    </li>
                  ))}
                </ol>
              )}

              {sec.code && (
                <pre style={{
                  background: '#0f172a', color: '#e2e8f0', padding: '20px 24px', borderRadius: 10,
                  fontSize: 13, lineHeight: 1.6, overflow: 'auto', marginBottom: 16,
                  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                }}>
                  <code>{sec.code}</code>
                </pre>
              )}

              {sec.note && (
                <div style={{
                  background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
                  padding: '12px 16px', fontSize: 13, color: '#92400e', lineHeight: 1.6,
                  display: 'flex', gap: 10,
                }}>
                  <span style={{ flexShrink: 0 }}>💡</span>
                  <span>{sec.note}</span>
                </div>
              )}
            </div>
          ))}

          <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '40px 0 32px' }} />

          {/* Prev / Next navigation */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 40 }}>
            {prevArticle ? (
              <Link
                to={`/knowledge-center/${prevArticle.slug}`}
                style={{
                  display: 'block', padding: '16px 20px', borderRadius: 10,
                  border: '1.5px solid #e2e8f0', textDecoration: 'none',
                  transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = section?.color}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
              >
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>← Previous</div>
                <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{prevArticle.title}</div>
              </Link>
            ) : <div />}

            {nextArticle ? (
              <Link
                to={`/knowledge-center/${nextArticle.slug}`}
                style={{
                  display: 'block', padding: '16px 20px', borderRadius: 10,
                  border: '1.5px solid #e2e8f0', textDecoration: 'none',
                  textAlign: 'right', transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = section?.color}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
              >
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>Next →</div>
                <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{nextArticle.title}</div>
              </Link>
            ) : <div />}
          </div>

          {/* Help CTA */}
          <div style={{ background: '#f8fafc', borderRadius: 12, padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Was this article helpful?</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Still have questions? Our support team is here to help.</div>
            </div>
            <a
              href="mailto:support@clicksaver.app"
              style={{ background: '#2563eb', color: '#fff', textDecoration: 'none', padding: '10px 22px', borderRadius: 8, fontSize: 13, fontWeight: 700, flexShrink: 0 }}
            >
              Contact Support
            </a>
          </div>
        </main>
      </div>
    </MarketingLayout>
  );
}
